import {
  Address,
  BenefitDependentTypeEnum,
  BenefitTypeEnum,
  CompanyBenefitPlanDTO,
  CompanyBenefitPlanTierCostDTO,
  GenderEnum,
  MarketplacePlanDTO,
  NameSuffixEnum,
  PendingCompanyBenefitEnrollmentDTO,
  SexEnum
} from "@vitable/asclepius-api-client-ts"
import { VPC_STEP, type VpcStep } from "@common/utilities/benefit-utils"

/** VPC Benefit election (has 1 benefit plan + 1 tier only; can be waived whole or per-dependent). */
export interface VpcBenefitElection {
  enrollment: PendingCompanyBenefitEnrollmentDTO
  vpcBenefitPlan: CompanyBenefitPlanDTO
  vpcTierCost: CompanyBenefitPlanTierCostDTO
  waived: boolean
  waivedDependentsIds?: Array<string>
}

/** Non-VPC Benefit plan selection for internal Vitable benefit plans. */
export interface NonVpcBenefitInternalPlanSelection {
  type: "internal"
  benefitPlan: CompanyBenefitPlanDTO
  tierCost: CompanyBenefitPlanTierCostDTO
}

/** Non-VPC Benefit plan selection for external marketplace plans. */
export interface NonVpcBenefitExternalPlanSelection {
  type: "external"
  externalPlan: MarketplacePlanDTO
  employerAllowanceInCents?: number // for contribution-based benefits
}

/** Non-VPC Benefit plan selection for Medicare. */
export interface NonVpcBenefitMedicareSelection {
  type: "medicare"
  employerAllowanceInCents?: number // for contribution-based benefits
}

/** Non-VPC Benefit pending plan shopping selection (user clicked opt-in but hasn't navigated to plan shopping yet). */
export interface NonVpcBenefitPendingPlanShoppingSelection {
  type: "pendingPlanShopping"
  employerAllowanceInCents?: number
}

/** Non-VPC Benefit plan selection (can be internal or external or waived). */
export type NonVpcBenefitPlanSelection =
  | NonVpcBenefitInternalPlanSelection // Selected an internal benefit plan
  | NonVpcBenefitExternalPlanSelection // Selected an external benefit plan
  | NonVpcBenefitMedicareSelection // Selected an ICHRA but has Medicare
  | NonVpcBenefitPendingPlanShoppingSelection // Pending plan shopping (opted-in but hasn't shopped yet)
  | undefined // No selection yet
  | null // Waived benefit

/** Non-VPC Benefit election (can be waived + has multiple tiers). */
export interface NonVpcBenefitElection {
  enrollment: PendingCompanyBenefitEnrollmentDTO
  selection: NonVpcBenefitPlanSelection
  waivedDependentsIds: Array<string>
}

/** Bundle of benefit elections packaged for one enrollment flow. */
export interface BenefitElectionsPackage {
  vpcElection: Optional<VpcBenefitElection> // undefined = no offered VPC Benefit OR renewal
  nonVpcElections: Array<NonVpcBenefitElection>
}

/** Every pending election of a company across its plan years; each enrollment flow covers one group of them. */
export interface CompanyPendingElections {
  vpcElections: Array<VpcBenefitElection>
  nonVpcElections: Array<NonVpcBenefitElection>
}

/** A plan reference with an optional SBC link. */
export interface BenefitElectionsPackagePlanRef {
  benefitPlanName: string
  sbcFileUrl: string | null
}

/** A single waived enrollment grouped by benefit name with the plans that were on offer. */
export interface BenefitElectionsPackageWaivedEnrollment {
  benefitName: string
  plans: Array<BenefitElectionsPackagePlanRef>
}

/** Collection of selected / waived elections for confirmation. */
export interface BenefitElectionsPackageConfirmation {
  selected: Array<BenefitElectionsPackagePlanRef>
  waived: Array<BenefitElectionsPackageWaivedEnrollment>
  /** True when at least one selected election is a non-VPC benefit (drives the QLE binding sentence). */
  selectedIncludesNonVpcIn: boolean
  /** True when at least one waived election is a non-VPC benefit (drives the QLE binding sentence). */
  waivedIncludesNonVpcIn: boolean
}

/** Total cost summary of all selected benefit elections. */
export interface BenefitElectionSelectionsCostSummary {
  selections: Array<{ label: string; cost: number; waived?: boolean }>
  totalCost: number
}

/**
 * A translatable string reference. Two shapes:
 *   - `{ key, params? }` — an i18n key routed through `$t()` (for catalog entries).
 *   - `{ literal }` — a runtime string rendered verbatim (for data like plan names
 *     or benefit names that must not be looked up as translation keys).
 */
export type TranslatableText = { key: string; params?: Record<string, string | number> } | { literal: string }

/** A legal dependent for a benefit election. */
export interface BenefitElectionDependent {
  legalDependentId: string
  dependentMemberId?: string
  firstName: string
  lastName: string
  suffix?: NameSuffixEnum
  dateOfBirth: string
  sex?: SexEnum
  gender?: GenderEnum
  email: string
  phone: string
  type?: BenefitDependentTypeEnum
  address?: Address
  maskedSsn: string
  encryptedSsn: string
}

/** A legal definition of family status for benefits. */
export enum BenefitFamilyStatusEnum {
  EMPLOYEE_ONLY = "EE",
  EMPLOYEE_PLUS_SPOUSE = "ES",
  EMPLOYEE_PLUS_CHILDREN = "EC",
  EMPLOYEE_PLUS_FAMILY = "EF"
}

/** Non-VPC elections grouped by coverage year. */
export interface NonVpcElectionsWithYear {
  year: number
  elections: Array<NonVpcBenefitElection>
}

/** A step in the enrollment benefit flow. VPC is not a BenefitTypeEnum, so we use a union. */
export type EnrollmentBenefitStep = BenefitTypeEnum | VpcStep

/** Every Vue Router name used in the enrollment flow. Scoped union for type-safe routing inside this module. */
export type EnrollRouteName =
  | "enroll:background"
  | "enroll:dependents"
  | "enroll:medical-benefit"
  | "enroll:vpc-benefit"
  | "enroll:dental-benefit"
  | "enroll:vision-benefit"
  | "enroll:ichra-shop-intro"
  | "enroll:choose-health-plan"
  | "enroll:carrier-questions"
  | "enroll:schedule-medicare-call"
  | "enroll:rec-gate"
  | "enroll:rec-survey"
  | "enroll:rec-thinking"
  | "enroll:rec-result"
  | "enroll:sign-forms"

/** Benefit steps that participate in the enrollment flow UI. Hospital exists in the API enum but isn't routed. */
export type RoutableEnrollmentBenefitStep = Exclude<EnrollmentBenefitStep, typeof BenefitTypeEnum.Hospital>

/** Sentinel step key for the end-of-flow ICHRA plan-shopping leg (primer + browse, or Medicare call). */
export const PLAN_SHOPPING_STEP = "plan-shopping" as const
export type PlanShoppingStep = typeof PLAN_SHOPPING_STEP

/** Stepper segment keys: every navigable step (fixed bookends, routable benefits, plan-shopping). */
export type EnrollmentStepKey =
  "background" | "dependents" | RoutableEnrollmentBenefitStep | PlanShoppingStep | "sign-forms"

/** Single source of truth for step → route-name binding across composables, guards, and the bar. */
export const ENROLLMENT_STEP_ROUTES = {
  background: "enroll:background",
  dependents: "enroll:dependents",
  [BenefitTypeEnum.Medical]: "enroll:medical-benefit",
  [VPC_STEP]: "enroll:vpc-benefit",
  [BenefitTypeEnum.Dental]: "enroll:dental-benefit",
  [BenefitTypeEnum.Vision]: "enroll:vision-benefit",
  [PLAN_SHOPPING_STEP]: "enroll:ichra-shop-intro",
  "sign-forms": "enroll:sign-forms"
} as const satisfies Record<EnrollmentStepKey, EnrollRouteName>

/**
 * Route-name binding for the medical-benefit step's sub-pages (ICHRA primer, plan shopping,
 * Medicare call scheduling). These are not steps in the stepper, so they're separated from
 * `ENROLLMENT_STEP_ROUTES`, but they share the same single-source-of-truth pattern.
 */
export const ENROLLMENT_SUBFLOW_ROUTES = {
  ichraShopIntro: "enroll:ichra-shop-intro",
  chooseHealthPlan: "enroll:choose-health-plan",
  carrierQuestions: "enroll:carrier-questions",
  scheduleMedicareCall: "enroll:schedule-medicare-call",
  recGate: "enroll:rec-gate",
  recSurvey: "enroll:rec-survey",
  recThinking: "enroll:rec-thinking",
  recResult: "enroll:rec-result"
} as const satisfies Record<string, EnrollRouteName>

/** Narrowing helper — true when a benefit step has a configured route. */
export function isRoutableBenefitStep(step: EnrollmentBenefitStep): step is RoutableEnrollmentBenefitStep {
  return step !== BenefitTypeEnum.Hospital
}

const ALL_ENROLL_ROUTE_NAMES: ReadonlySet<string> = new Set([
  ...Object.values(ENROLLMENT_STEP_ROUTES),
  ...Object.values(ENROLLMENT_SUBFLOW_ROUTES)
])

/** Runtime guard — narrows arbitrary input to a typed enrollment route name. */
export function isEnrollRouteName(value: unknown): value is EnrollRouteName {
  return typeof value === "string" && ALL_ENROLL_ROUTE_NAMES.has(value)
}

declare module "vue-router" {
  interface RouteMeta {
    stepKey?: EnrollmentStepKey
    benefitType?: EnrollmentBenefitStep
    parentStepKey?: EnrollmentStepKey
    subflowBack?: "parent" | "previous"
  }
}
